import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UtensilsCrossed, MessageCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goTab = (t) => { setTab(t); setErr(""); setMsg(""); };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      if (tab === "login") { const u = await login(form.email, form.password); nav(u?.role === "superadmin" ? "/admin" : "/app"); }
      else if (tab === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setMsg(data.message);
      }
    } catch (e) { setErr(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#C84B27,#E05A36)" }}>
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/20"><UtensilsCrossed className="h-6 w-6" /></div>
          <span className="font-heading font-bold text-lg">turismo.latina.it</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Prenotazioni + WhatsApp per la tua attività</h1>
          <p className="mt-4 text-white/90 text-lg max-w-md">Ricevi i clienti dal portale, gestisci l'agenda del giorno e invia conferme automatiche su WhatsApp. Ristoranti, B&B, agriturismi e hotel del litorale pontino.</p>
          <div className="mt-6 flex items-center gap-2 rounded-xl bg-white/15 px-4 py-3 w-fit">
            <MessageCircle className="h-5 w-5" /> Conferme e promemoria automatici
          </div>
        </div>
        <p className="text-white/70 text-sm">© turismo.latina.it — Circuito attività ricettive</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-white"><UtensilsCrossed className="h-6 w-6" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">turismo.latina.it</span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">{tab === "login" ? "Accedi al gestionale" : "Recupera la password"}</h2>
          <p className="text-slate-500 mt-1 text-sm">{tab === "forgot" ? "Inserisci la tua email: ti invieremo un link per reimpostare la password." : "Gestisci le prenotazioni della tua attività."}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>Email</Label><Input data-testid="login-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@email.it" required /></div>
            {tab !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label>Password</Label>
                  {tab === "login" && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => goTab("forgot")} className="text-sm font-medium text-primary hover:underline">Password dimenticata?</button>
                  )}
                </div>
                <Input data-testid="login-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" required />
              </div>
            )}
            {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
            {msg && <p data-testid="forgot-message" className="text-sm text-emerald-600">{msg}</p>}
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full rounded-xl h-11">
              {loading ? "Attendi..." : tab === "login" ? "Accedi" : "Invia link di recupero"}
            </Button>
            {tab === "login" && (
              <p className="text-center text-sm text-slate-500">Non hai un account? Contatta turismo.latina.it per attivare la tua attività.</p>
            )}
            {tab === "forgot" && (
              <button type="button" onClick={() => goTab("login")} className="w-full text-sm text-slate-500 hover:text-slate-900">← Torna all'accesso</button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
